import { create } from 'zustand';
import { http } from '@/libs/http';
import type { Permission, PermissionCreate, PermissionUpdate } from '@/types';
import type { PagenatedResponse } from '@/types/api';
import { toast } from 'sonner';

interface PermissionTreeNode {
    resource: string;
    permissions: Array<Permission & { action?: string }>;
}

interface PermissionTree {
    tree: PermissionTreeNode[];
    total_resources: number;
    total_permissions: number;
}

interface PermissionValidation {
    permission_id: string;
    is_valid: boolean;
    errors: string[];
    warnings: string[];
    implicit_dependencies: string[];
}

interface PermissionStats {
    permission_id: string;
    permission_name: string;
    role_count: number;
    indirect_user_count: number;
}

interface PermissionState {
    permissions: Permission[];
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    list: (filter?: (permissions: Permission[]) => Permission[]) => Promise<Permission[]>;
    get: (id: string) => Promise<Permission>;
    create: (data: PermissionCreate) => Promise<void>;
    update: (id: string, data: PermissionUpdate) => Promise<void>;
    delete: (id: string) => Promise<void>;
    select: (criteria: (permissions: Permission[]) => Permission) => Permission;

    // Additional actions
    clone: (id: string, name: string) => Promise<void>;
    
    // New methods for Epic #109
    getTree: () => Promise<PermissionTree>;
    validate: (permissionId: string, roleScopes: string[], existingPermissions: string[]) => Promise<PermissionValidation>;
    getStats: (permissionId: string) => Promise<PermissionStats>;
}

export const usePermissions = create<PermissionState>((set, get) => ({
    permissions: [],
    isLoading: false,
    error: null,

    list: async (filter) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.get<PagenatedResponse<Permission>>('/mgmt/v1/permissions');
            const data = response.items;
            set({ permissions: data, isLoading: false });
            return filter ? filter(data) : data;
        } catch (err: any) {
            set({ error: err.message, isLoading: false });
            return [];
        }
    },

    get: async (id) => {
        const found = get().permissions.find((permission) => permission.id === id);
        if (found) return found;
        const data = await http.get<Permission>(`/mgmt/v1/permissions/${id}`);
        set({ permissions: [...get().permissions.filter((p) => p.id !== id), data] });
        return data;
    },

    create: async (data) => {
        const previousPermissions = get().permissions;
        const tempId = `temp-${Date.now()}`;
        const tempPermission = { ...data, id: tempId } as unknown as Permission;
        
        set({ permissions: [...previousPermissions, tempPermission] });

        try {
            const newPermission = await http.post<Permission>('/mgmt/v1/permissions', data);
            set({ 
                permissions: get().permissions.map(p => p.id === tempId ? newPermission : p) 
            });
        } catch (err: any) {
            set({ permissions: previousPermissions, error: err.message });
            throw err;
        }
    },

    update: async (id, data) => {
        const previousPermissions = get().permissions;
        set({
            permissions: previousPermissions.map(p => p.id === id ? { ...p, ...data } : p) as Permission[]
        });

        try {
            const updatedPermission = await http.put<Permission>(`/mgmt/v1/permissions/${id}`, data);
            set({
                permissions: get().permissions.map(p => p.id === id ? updatedPermission : p)
            });
        } catch (err: any) {
            set({ permissions: previousPermissions, error: err.message });
            throw err;
        }
    },

    delete: async (id) => {
        const previousPermissions = get().permissions;
        set({
            permissions: previousPermissions.filter(p => p.id !== id)
        });

        try {
            await http.delete(`/mgmt/v1/permissions/${id}`);
        } catch (err: any) {
            set({ permissions: previousPermissions, error: err.message });
            throw err;
        }
    },

    select: (criteria) => {
        return criteria(get().permissions);
    },

    clone: async (id, name) => {
        const newPermission = await http.post<Permission>(`/mgmt/v1/permissions/${id}/clone`, { name });
        set({ permissions: [...get().permissions, newPermission] });
    },
    
    // New methods for Epic #109
    getTree: async () => {
        try {
            return await http.get<PermissionTree>('/mgmt/v1/permissions/tree/structure');
        } catch (err: any) {
            toast.error('Failed to load permission tree');
            throw err;
        }
    },
    
    validate: async (permissionId, roleScopes, existingPermissions) => {
        try {
            return await http.post<PermissionValidation>(
                `/mgmt/v1/permissions/${permissionId}/validate`,
                {
                    role_applicable_scopes: roleScopes,
                    existing_permissions: existingPermissions
                }
            );
        } catch (err: any) {
            toast.error('Failed to validate permission');
            throw err;
        }
    },
    
    getStats: async (permissionId) => {
        try {
            return await http.get<PermissionStats>(`/mgmt/v1/permissions/${permissionId}/stats`);
        } catch (err: any) {
            toast.error('Failed to load permission statistics');
            throw err;
        }
    }
}));

export const usePermission = (id?: string) => {
    const permission = usePermissions((state) =>
        id ? state.permissions.find((item) => item.id === id) ?? null : null,
    );

    const ensureId = (): string => {
        if (!id) throw new Error('Permission id is required.');
        return id;
    };

    return {
        permission,
        get: () => usePermissions.getState().get(ensureId()),
        update: (data: PermissionUpdate) => usePermissions.getState().update(ensureId(), data),
        delete: () => usePermissions.getState().delete(ensureId()),
    };
};
